import React, { useState } from "react";
import {
  Bar,
  BarChart,
  XAxis,
  YAxis,
  ResponsiveContainer,
  CartesianGrid,
  Cell,
} from "recharts";
import { CircleNotch, Check, CaretDown } from "phosphor-react";
import { ReactComponent as MemberLogo } from "assets/images/member.svg";

const options = ["Last 7 Days", "Last 10 Days", "Last 20 Days"];

function SurveyStats() {
  const surveyStats = {
    totalQuestions: 50,
    completedSurveys: 120,
    pendingSurveys: 30,
    inProgressSurveys: 25,
    activeMembersCount: 87,
  };

  const [ecmSelectedOption, setEcmSelectedOption] = useState("Last 10 Days");
  const [surveyStaffSelectedOption, setSurveyStaffSelectedOption] = useState(
    "Last 10 Days"
  );
  const [ecmIsOpen, setEcmIsOpen] = useState(false);
  const [surveyStaffIsOpen, setSurveyStaffIsOpen] = useState(false);

  const ecmData = [
    { day: "07/18", Enrolled: 10, Outreach: 5, Excluded: 2 },
    { day: "07/19", Enrolled: 12, Outreach: 4, Excluded: 1 },
    { day: "07/20", Enrolled: 14, Outreach: 6, Excluded: 0 },
    { day: "07/21", Enrolled: 9, Outreach: 3, Excluded: 2 },
    { day: "07/22", Enrolled: 15, Outreach: 2, Excluded: 1 },
    { day: "07/23", Enrolled: 11, Outreach: 4, Excluded: 3 },
    { day: "07/24", Enrolled: 13, Outreach: 5, Excluded: 1 },
    { day: "07/25", Enrolled: 10, Outreach: 5, Excluded: 2 },
    { day: "07/26", Enrolled: 12, Outreach: 4, Excluded: 1 },
    { day: "07/27", Enrolled: 14, Outreach: 6, Excluded: 0 },
    { day: "07/28", Enrolled: 9, Outreach: 3, Excluded: 2 },
    { day: "07/29", Enrolled: 15, Outreach: 2, Excluded: 1 },
    { day: "07/30", Enrolled: 11, Outreach: 4, Excluded: 3 },
    { day: "07/31", Enrolled: 13, Outreach: 5, Excluded: 1 },
  ];

  const surveyStaffData = [
    { day: "07/18", staff_name: "John", survey_count: 3 },
    { day: "07/19", staff_name: "Jane", survey_count: 4 },
    { day: "07/20", staff_name: "Alex", survey_count: 2 },
    { day: "07/21", staff_name: "Sara", survey_count: 5 },
    { day: "07/22", staff_name: "Mark", survey_count: 3 },
    { day: "07/23", staff_name: "John", survey_count: 2 },
    { day: "07/24", staff_name: "Jane", survey_count: 1 },
    { day: "07/25", staff_name: "Alex", survey_count: 3 },
    { day: "07/26", staff_name: "Sara", survey_count: 4 },
    { day: "07/27", staff_name: "Mark", survey_count: 3 },
    { day: "07/28", staff_name: "John", survey_count: 3 },
    { day: "07/29", staff_name: "Jane", survey_count: 5 },
    { day: "07/30", staff_name: "Alex", survey_count: 3 },
    { day: "07/31", staff_name: "Sara", survey_count: 4 },
  ];

  const getDaysFromOption = (option) => parseInt(option.match(/\d+/)[0]);

  const filteredEcmData = ecmData.slice(
    Math.max(ecmData.length - getDaysFromOption(ecmSelectedOption), 0)
  );

  const filteredSurveyStaffDataRaw = surveyStaffData.slice(
    Math.max(
      surveyStaffData.length - getDaysFromOption(surveyStaffSelectedOption),
      0
    )
  );

  // Aggregate staff survey counts
  const staffMap = {};
  filteredSurveyStaffDataRaw.forEach(({ staff_name, survey_count }) => {
    if (!staffMap[staff_name]) {
      staffMap[staff_name] = 0;
    }
    staffMap[staff_name] += survey_count;
  });

  const filteredSurveyStaffData = Object.entries(staffMap).map(
    ([staff_name, survey_count]) => ({
      staff_name,
      survey_count,
    })
  );

  return (
    <div className="row">
      {[
        {
          label: "Active MIF Members",
          value: surveyStats.activeMembersCount,
          Icon: MemberLogo,
        },
        {
          label: "Surveys completed",
          value: surveyStats.completedSurveys,
          Icon: Check,
        },
        {
          label: "Surveys in progress",
          value: surveyStats.inProgressSurveys,
          Icon: CircleNotch,
        },
      ].map(({ label, value, Icon }, i) => (
        <div
          key={i}
          className="col-6 col-md-6 col-lg-6 col-xl-6 col-xxl-3 mb-3 state-card-container"
        >
          <div className="state-card">
            <div className="d-flex flex-column">
              <p className="opacity-50">{label}</p>
              <div className="d-flex justify-content-between align-items-center flex-row">
                <p className="f-w">{value}</p>
                <Icon size={40} className="clr" />
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Charts */}
      <div className="container-fluid p-2.5 mt-3">
        <div className="row g-4">
          {/* ECM Chart */}
          <div className="col-12 col-lg-6">
            <div className="bg-white rounded-3 chart-p h-100">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <p className="fw-bold mb-0 dc-h">No. Surveys Submitted</p>
                <div
                  className="custom-select"
                  onClick={() => setEcmIsOpen(!ecmIsOpen)}
                >
                  <span>{ecmSelectedOption}</span>
                  <span
                    className="chart-caret"
                    style={{
                      transform: ecmIsOpen
                        ? "rotate(180deg) translateY(-12%)"
                        : "rotate(0deg)",
                      transition: "transform 0.2s ease-in-out",
                    }}
                  >
                    <CaretDown weight="bold" />
                  </span>
                  {ecmIsOpen && (
                    <ul className="chart-dropdown">
                      {options.map((option, index) => (
                        <li
                          className="chart-dropdown-list"
                          key={index}
                          onClick={() => {
                            setEcmSelectedOption(option);
                            setEcmIsOpen(false);
                          }}
                        >
                          {option}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              <div style={{ height: "360px" }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={filteredEcmData}>
                    <CartesianGrid vertical={false} />
                    <XAxis
                      dataKey="day"
                      axisLine={false}
                      tickLine={false}
                      tickMargin={15}
                    />
                    <YAxis
                      width={30}
                      tickCount={7}
                      axisLine={false}
                      tickLine={false}
                      allowDecimals={false}
                    />
                    <Bar
                      dataKey="Enrolled"
                      stackId="1"
                      fill="#22C55E"
                      barSize={21.28}
                    >
                      {filteredEcmData.map((el, index) =>
                        el.Outreach === 0 && el.Excluded === 0 ? (
                          <Cell radius={[40, 40, 0, 0]} key={index} />
                        ) : (
                          <Cell radius={[0, 0, 0, 0]} key={index} />
                        )
                      )}
                    </Bar>
                    <Bar
                      dataKey="Outreach"
                      stackId="1"
                      fill="#EAB308"
                      barSize={21.28}
                    >
                      {filteredEcmData.map((el, index) =>
                        el.Excluded === 0 ? (
                          <Cell radius={[40, 40, 0, 0]} key={index} />
                        ) : (
                          <Cell radius={[0, 0, 0, 0]} key={index} />
                        )
                      )}
                    </Bar>
                    <Bar
                      dataKey="Excluded"
                      stackId="1"
                      fill="#EF4444"
                      barSize={21.28}
                      radius={[40, 40, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="d-flex gap-5">
                <p className="enrolled">Enrolled</p>
                <p className="pendings">Outreach</p>
                <p className="excluded">Excluded</p>
              </div>
            </div>
          </div>

          {/* Survey Staff Chart */}
          <div className="col-12 col-lg-6">
            <div className="bg-white rounded-3 p-4 h-100">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <p className="fw-bold mb-0 dc-h">No. of Surveys Completed</p>
                <div
                  className="custom-select"
                  onClick={() => setSurveyStaffIsOpen(!surveyStaffIsOpen)}
                >
                  <span>{surveyStaffSelectedOption}</span>
                  <span
                    className="chart-caret"
                    style={{
                      transform: surveyStaffIsOpen
                        ? "rotate(180deg) translateY(-12%)"
                        : "rotate(0deg)",
                      transition: "transform 0.2s ease-in-out",
                    }}
                  >
                    <CaretDown weight="bold" />
                  </span>
                  {surveyStaffIsOpen && (
                    <ul className="chart-dropdown">
                      {options.map((option, index) => (
                        <li
                          className="chart-dropdown-list"
                          key={index}
                          onClick={() => {
                            setSurveyStaffSelectedOption(option);
                            setSurveyStaffIsOpen(false);
                          }}
                        >
                          {option}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              <div style={{ height: "360px" }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={filteredSurveyStaffData}>
                    <CartesianGrid vertical={false} />
                    <XAxis
                      dataKey="staff_name"
                      axisLine={false}
                      tickLine={false}
                      tickMargin={15}
                    />
                    <YAxis
                      width={30}
                      tickCount={7}
                      axisLine={false}
                      tickLine={false}
                      allowDecimals={false}
                    />
                    <Bar
                      dataKey="survey_count"
                      stackId="1"
                      fill="#22C55E"
                      barSize={21.28}
                      radius={[40, 40, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SurveyStats;
